import React from "react";
import clsx from "clsx";

// Progress Bar Types
export interface ProgressBarProps {
  value?: number;
  max?: number;
  variant?: "default" | "striped" | "animated";
  state?: "default" | "success" | "warning" | "error";
  size?: "sm" | "md" | "lg";
  label?: string;
  showLabel?: boolean;
  indeterminate?: boolean;
  className?: string;
}

// Progress Circle Types
export interface ProgressCircleProps {
  value?: number;
  max?: number;
  size?: "sm" | "md" | "lg";
  state?: "default" | "success" | "warning" | "error";
  label?: string;
  showLabel?: boolean;
  indeterminate?: boolean;
  strokeWidth?: number;
  className?: string;
}

// Skeleton Types
export interface SkeletonProps {
  variant?: "line" | "block" | "circle";
  animated?: boolean;
  className?: string;
  children?: React.ReactNode;
}

// Progress Bar Component
export const ProgressBar: React.FC<ProgressBarProps> = ({
  value = 0,
  max = 100,
  variant = "default",
  state = "default",
  size = "md",
  label,
  showLabel = false,
  indeterminate = false,
  className,
}) => {
  const percentage = Math.min(Math.max((value / max) * 100, 0), 100);

  const sizeStyles = {
    sm: "h-1.5",
    md: "h-2.5",
    lg: "h-4",
  };

  const stateStyles = {
    default: "bg-tracq-bright-blue",
    success: "bg-green-500",
    warning: "bg-yellow-500",
    error: "bg-red-600",
  };

  const variantStyles = {
    default: "",
    striped: "bg-stripes",
    animated: indeterminate ? "animate-pulse" : "",
  };

  const barClasses = clsx(
    "rounded-full transition-all duration-300 ease-in-out",
    sizeStyles[size],
    stateStyles[state],
    variantStyles[variant],
    {
      "bg-gradient-to-r from-tracq-bright-blue to-tracq-deep-orange animate-pulse": indeterminate,
    },
  );

  const trackClasses = clsx(
    "w-full bg-gray-800 rounded-full overflow-hidden",
    sizeStyles[size],
    className,
  );

  return (
    <div className="w-full">
      {/* Label above progress bar */}
      {showLabel && label && (
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm text-gray-200">{label}</span>
          {!indeterminate && (
            <span className="text-sm text-gray-400">{Math.round(percentage)}%</span>
          )}
        </div>
      )}

      {/* Progress bar */}
      <div className={trackClasses}>
        {indeterminate ? (
          <div className={clsx(barClasses, "w-full animate-pulse")} />
        ) : (
          <div className={barClasses} style={{ width: `${percentage}%` }} />
        )}
      </div>

      {/* Label inside progress bar */}
      {!showLabel && label && !indeterminate && (
        <div className="relative -mt-6 flex items-center justify-center">
          <span className="text-xs font-medium text-white bg-black bg-opacity-50 px-2 py-1 rounded">
            {label}
          </span>
        </div>
      )}
    </div>
  );
};

// Progress Circle Component
export const ProgressCircle: React.FC<ProgressCircleProps> = ({
  value = 0,
  max = 100,
  size = "md",
  state = "default",
  label,
  showLabel = false,
  indeterminate = false,
  strokeWidth,
  className,
}) => {
  // Ensure value and max are valid numbers
  const validValue = typeof value === "number" && !isNaN(value) ? value : 0;
  const validMax = typeof max === "number" && !isNaN(max) && max > 0 ? max : 100;
  const percentage = Math.min(Math.max((validValue / validMax) * 100, 0), 100);

  const sizeStyles = {
    sm: { size: "w-6 h-6", radius: 20, strokeWidth: 2, svgSize: 24 },
    md: { size: "w-10 h-10", radius: 40, strokeWidth: 3, svgSize: 40 },
    lg: { size: "w-16 h-16", radius: 60, strokeWidth: 4, svgSize: 64 },
  };

  const stateStyles = {
    default: "stroke-tracq-bright-blue",
    success: "stroke-green-500",
    warning: "stroke-yellow-500",
    error: "stroke-red-600",
  };

  const currentSize = sizeStyles[size];
  const currentRadius = strokeWidth ? currentSize.radius - strokeWidth / 2 : currentSize.radius;
  const currentCircumference = 2 * Math.PI * currentRadius;
  const currentStrokeDashoffset = currentCircumference - (percentage / 100) * currentCircumference;

  // Calculate SVG size: radius * 2 + strokeWidth for padding
  const svgSize = currentSize.radius * 2 + (strokeWidth || currentSize.strokeWidth) + 4;
  const centerX = svgSize / 2;
  const centerY = svgSize / 2;

  return (
    <div
      className={clsx(
        "relative inline-flex items-center justify-center",
        currentSize.size,
        className,
      )}
    >
      <svg className="transform -rotate-90" width={svgSize} height={svgSize}>
        {/* Background circle */}
        <circle
          cx={centerX}
          cy={centerY}
          r={currentRadius}
          stroke="#374151"
          strokeWidth={strokeWidth || currentSize.strokeWidth}
          fill="transparent"
        />

        {/* Progress circle */}
        {indeterminate ? (
          <circle
            cx={centerX}
            cy={centerY}
            r={currentRadius}
            stroke="url(#gradient)"
            strokeWidth={strokeWidth || currentSize.strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            strokeDasharray={`${currentCircumference * 0.25} ${currentCircumference}`}
            className="animate-spin"
            style={{
              strokeDashoffset: currentCircumference * 0.25,
            }}
          />
        ) : (
          <circle
            cx={centerX}
            cy={centerY}
            r={currentRadius}
            stroke="currentColor"
            strokeWidth={strokeWidth || currentSize.strokeWidth}
            fill="transparent"
            strokeLinecap="round"
            className={clsx("transition-all duration-300 ease-in-out", stateStyles[state])}
            strokeDasharray={currentCircumference}
            strokeDashoffset={currentStrokeDashoffset}
          />
        )}

        {/* Gradient for indeterminate */}
        {indeterminate && (
          <defs>
            <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#009FFD" />
              <stop offset="100%" stopColor="#FF6B00" />
            </linearGradient>
          </defs>
        )}
      </svg>

      {/* Label in center */}
      {(showLabel || label) && !indeterminate && (
        <div className="absolute inset-0 flex items-center justify-center">
          <span className="text-xs font-medium text-gray-200">
            {label || `${Math.round(percentage)}%`}
          </span>
        </div>
      )}
    </div>
  );
};

// Skeleton Component
export const Skeleton: React.FC<SkeletonProps> = ({
  variant = "line",
  animated = true,
  className,
  children,
}) => {
  const variantStyles = {
    line: "h-4 w-full rounded",
    block: "h-32 w-full rounded-lg",
    circle: "h-12 w-12 rounded-full",
  };

  const baseClasses = clsx(
    "bg-gray-800",
    variantStyles[variant],
    {
      "animate-pulse": animated,
    },
    className,
  );

  if (children) {
    return <div className={baseClasses}>{children}</div>;
  }

  return <div className={baseClasses} />;
};

// Specialized Skeleton Components
export const SkeletonLine: React.FC<Omit<SkeletonProps, "variant">> = (props) => (
  <Skeleton {...props} variant="line" />
);

export const SkeletonBlock: React.FC<Omit<SkeletonProps, "variant">> = (props) => (
  <Skeleton {...props} variant="block" />
);

export const SkeletonCircle: React.FC<Omit<SkeletonProps, "variant">> = (props) => (
  <Skeleton {...props} variant="circle" />
);

// Skeleton Text Component
export const SkeletonText: React.FC<{
  lines?: number;
  className?: string;
  animated?: boolean;
}> = ({ lines = 3, className, animated = true }) => {
  return (
    <div className={clsx("space-y-2", className)}>
      {Array.from({ length: lines }).map((_, index) => (
        <SkeletonLine
          key={index}
          animated={animated}
          className={clsx({
            "w-full": index === lines - 1, // Last line shorter
            "w-3/4": index !== lines - 1,
          })}
        />
      ))}
    </div>
  );
};

// Skeleton Card Component
export const SkeletonCard: React.FC<{
  className?: string;
  animated?: boolean;
}> = ({ className, animated = true }) => {
  return (
    <div className={clsx("p-4 space-y-4", className)}>
      <div className="flex items-center space-x-4">
        <SkeletonCircle animated={animated} />
        <div className="space-y-2 flex-1">
          <SkeletonLine animated={animated} className="w-1/4" />
          <SkeletonLine animated={animated} className="w-1/2" />
        </div>
      </div>
      <SkeletonBlock animated={animated} />
      <SkeletonText lines={2} animated={animated} />
    </div>
  );
};
